import React, { useState } from 'react';
import { 
  ERROR_CATEGORIES, 
  ERROR_SEVERITY, 
  ERROR_RECOVERY_STRATEGIES 
} from '../utils/consolidatedErrorHandling.js';

/**
 * Enhanced error display component with recovery options and user-friendly messaging
 */
const EnhancedErrorDisplay = ({ 
  error, 
  onRetry, 
  onDismiss, 
  onRecovery,
  showTechnicalDetails = false,
  className='',
  compact = false 
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  if (!error) return null;

  const handleRetry = async () => {
    if (onRetry && error.isRetryable) {
      setIsRetrying(true);
      try {
        await onRetry();
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const handleRecovery = async (strategy) => {
    if (onRecovery) {
      await onRecovery(strategy);
    }
  };

  const getErrorIcon = () => {
    switch (error.category) {
      case ERROR_CATEGORIES.NETWORK:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case ERROR_CATEGORIES.AUTHENTICATION:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case ERROR_CATEGORIES.AUTHORIZATION:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
          </svg>
        );
      case ERROR_CATEGORIES.VALIDATION:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
    }
  };

  const getSeverityColor = () => {
    switch (error.severity) {
      case ERROR_SEVERITY.LOW:
        return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800';
      case ERROR_SEVERITY.MEDIUM:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-800';
      case ERROR_SEVERITY.HIGH:
        return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800';
      case ERROR_SEVERITY.CRITICAL:
        return 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-900/20 dark:border-gray-700';
    }
  };

  const getRecoveryStrategies = () => {
    const strategies = [];
    
    if (error.isRetryable) {
      strategies.push({
        type: ERROR_RECOVERY_STRATEGIES.RETRY,
        label: 'Try Again',
        primary: true
      });
    }
    
    if (error.hasFallback) {
      strategies.push({
        type: ERROR_RECOVERY_STRATEGIES.FALLBACK,
        label: 'Use Fallback'
      });
    }
    
    if (error.category === ERROR_CATEGORIES.AUTHENTICATION) {
      strategies.push({
        type: ERROR_RECOVERY_STRATEGIES.REDIRECT,
        label: 'Sign In',
        url: '/sign-in'
      });
    }
    
    if (error.category === ERROR_CATEGORIES.CONFLICT) {
      strategies.push({
        type: ERROR_RECOVERY_STRATEGIES.REFRESH,
        label: 'Refresh Page'
      });
    }
    
    return strategies;
  };

  const recoveryStrategies = getRecoveryStrategies();

  if (compact) {
    return (
      <div className={`flex items-center space-x-2 p-2 rounded-md border ${getSeverityColor()} ${className}`}>
        <div className="flex-shrink-0">
          {getErrorIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {error.userMessage}
          </p>
        </div>
        {error.isRetryable && (
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="flex-shrink-0 text-sm font-medium hover:underline disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-sm hover:opacity-75"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${getSeverityColor()} ${className}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {getErrorIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium">
                {error.category === ERROR_CATEGORIES.NETWORK && 'Connection Problem'}
                {error.category === ERROR_CATEGORIES.AUTHENTICATION && 'Authentication Required'}
                {error.category === ERROR_CATEGORIES.AUTHORIZATION && 'Access Denied'}
                {error.category === ERROR_CATEGORIES.VALIDATION && 'Invalid Input'}
                {error.category === ERROR_CATEGORIES.EXTERNAL_SERVICE && 'Service Unavailable'}
                {error.category === ERROR_CATEGORIES.RATE_LIMIT && 'Rate Limited'}
                {error.category === ERROR_CATEGORIES.NOT_FOUND && 'Not Found'}
                {error.category === ERROR_CATEGORIES.CONFLICT && 'Conflict'}
                {error.category === ERROR_CATEGORIES.SYSTEM && 'System Error'}
              </h3>
              <p className="mt-1 text-sm opacity-90">
                {error.userMessage}
              </p>
            </div>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="flex-shrink-0 ml-2 hover:opacity-75"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Recovery Actions */}
          {recoveryStrategies.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {recoveryStrategies.map((strategy, index) => (
                <button
                  key={index}
                  onClick={() => strategy.type === ERROR_RECOVERY_STRATEGIES.RETRY ? handleRetry() : handleRecovery(strategy)}
                  disabled={strategy.type === ERROR_RECOVERY_STRATEGIES.RETRY && isRetrying}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    strategy.primary
                      ? 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 dark:border-gray-600'
                      : 'bg-transparent hover:bg-black/5 dark:hover:bg-white/5'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {strategy.type === ERROR_RECOVERY_STRATEGIES.RETRY && isRetrying && (
                    <svg className="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {strategy.label}
                </button>
              ))}
            </div>
          )}
          
          {/* Technical Details Toggle */}
          {showTechnicalDetails && error.technicalDetails && (
            <div className="mt-3">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs font-medium hover:underline"
              >
                {showDetails ? 'Hide' : 'Show'} Technical Details
              </button>
              
              {showDetails && (
                <div className="mt-2 p-2 bg-black/5 dark:bg-white/5 rounded text-xs font-mono dark:bg-gray-800">
                  <div><strong>Code:</strong> {error.code}</div>
                  <div><strong>Status:</strong> {error.status}</div>
                  <div><strong>Timestamp:</strong> {error.timestamp}</div>
                  {error.technicalDetails && (
                    <div className="mt-1">
                      <strong>Details:</strong>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {typeof error.technicalDetails === 'string' 
                          ? error.technicalDetails 
                          : JSON.stringify(error.technicalDetails, null, 2)
                        }
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedErrorDisplay;
